import React, { useState, useEffect, useMemo } from 'react';
import './App.css';
import ManualHedge from './ManualHedge';
import SignalDCA from './SignalDCA';
import SignalPartialTakeProfit from './SignalPartialTakeProfit';
import HedgePartialTakeProfit from './HedgePartialTakeProfit';
import  SignalPositionDetails from './SignalPositionDetails';
import HedgePositionDetails from './HedgePositionDetails';
import HedgeOffsetLevelDetails from './HedgeOffsetLevel/HedgeOffsetLevelDetails';
import SpecialConfigurationDetails from './Specialconfiguration/SpecialConfigurationDetails';
import SignalResistanceLevel from './SignalResistanceLevel';
import HedgeResistanceLevel from './HedgeResistanceLevel';
import RepelTradeHedgeCloserModal from './RepelTradeHedgeCloserModal';
import HedgeToSignalConversionModal from './HedgeToSignalConversionModal';
import { buildScopedApiUrl, ensureArray, getApiHeaders } from "./config";
import './PositionManView.css';
import {  useNavigate } from 'react-router-dom';
import { useWorkspace } from './workspace/WorkspaceContext';

function PositionMainView() {
  const { workspace } = useWorkspace();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [signalRequests, setSignalRequests] = useState([]); 
  const [signalRequestsDynamic, setSignalRequestsDynamic] = useState([]); 
  const [actionSymbol, setActionSymbol] = useState(null);
  const [isManualHedgeModalOpen, setIsManualHedgeModalOpen] = useState(false);
  const [isSignalDCAModalOpen, setIsSignalDCAModalOpen] = useState(false);
  const [isSignalTPModalOpen, setIsSignalTPModalOpen] = useState(false);
  const [isHedgeTPModalOpen, setIsHedgeTPModalOpen] = useState(false);
  const [isSignalResistanceLevelModalOpen, setIsSignalResistanceLevelModalOpen] = useState(false);
  const [isHedgeResistanceLevelModalOpen, setIsHedgeResistanceLevelModalOpen] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [signalPositionrefreshTrigger, setSignalPositionrefreshTrigger] = useState(0);
  const [hedgePositionrefreshTrigger, setHedgePositionrefreshTrigger] = useState(0);
  const [isRepelTradeHedgeCloserModalOpen, setIsRepelTradeHedgeCloserModalOpen] = useState(false);
  const [isHedgeToSignalConvertModalOpen, setIsHedgeToSignalConvertModalOpen] = useState(false);
  const [openPositions, setOpenPositions] = useState([]);
  const [positionsNotice, setPositionsNotice] = useState('');
  const [sortBy,  setSortBy]  = useState('');    // '', 'symbol', 'unRealizedPL', or 'margin'
  const [sortDir, setSortDir] = useState('asc'); // 'asc' or 'desc'

  
  const navigate = useNavigate();

  const [availableSymbols, setAvailableSymbols] = useState([]);
  const [selectedSymbol, setSelectedSymbol] = useState('ALL');
 
  const unifiedButtonStyle = {
    padding: '6px 12px',
    fontSize: '13px',
    margin: '5px 8px 5px 0',
    borderRadius: '4px',
    border: 'none',
    backgroundColor: 'rgba(57, 65, 65, 0.85)', // equivalent of #394141da
    color: '#fff',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  };

  const sortedRequests = useMemo(() => {
    if (!sortBy) return signalRequestsDynamic;
 
    return [...signalRequestsDynamic].sort((a, b) => {
      let aVal, bVal;
 
      if (sortBy === 'symbol') {
        // sort by symbol string
        aVal = a.symbol.toLowerCase();
        bVal = b.symbol.toLowerCase();
      } else {
        // look up the matching openPosition for each signalRequest
        const posA = openPositions.find(p => p.symbol === a.symbol) || {};
        const posB = openPositions.find(p => p.symbol === b.symbol) || {};
 
        if (sortBy === 'margin') {
          aVal = parseFloat(posA.margin)       || 0;
          bVal = parseFloat(posB.margin)       || 0;
        } else /* unRealizedPL */ {
          aVal = parseFloat(posA.unrealizedPL) || 0;
          bVal = parseFloat(posB.unrealizedPL) || 0;
        }
      }
 
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ?  1 : -1;
      return 0;
    });
  }, [signalRequestsDynamic, openPositions, sortBy, sortDir]);
  


  useEffect(() => {
    const intervalId = setInterval(() => {
      setRefreshTrigger(prev => prev + 1); // Increment the trigger to cause a re-render
    }, 10000); // 10 seconds
  
    return () => clearInterval(intervalId); // Clear interval on component unmount
  }, []);


  // Fetch signal requests
  useEffect(() => {
    // Fetch active signal requests
    fetch(buildScopedApiUrl('/api/bot/ui/get/getSignalRequests', workspace),
      {
        headers: getApiHeaders()
      })
      .then(response => response.json())
      .then(data => {
        setSignalRequests(ensureArray(data));
      })
      .catch(error => {
        setError(error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [workspace]);

  useEffect(() => {
    const fetchOpenPositions = async () => {
      try {
        const response = await fetch(buildScopedApiUrl('/api/bot/ui/get/allOpenPositionsAlternative', workspace),
          {
            headers: getApiHeaders()
          });
        const data = await response.json();
        const normalizedPositions = ensureArray(data);
        if (!Array.isArray(data)) {
          console.warn('Unexpected open positions response shape for workspace', workspace, data);
        }
        setOpenPositions(normalizedPositions);
        setPositionsNotice(
          normalizedPositions.length === 0 && !Array.isArray(data)
            ? 'Open positions are unavailable for the selected workspace right now.'
            : ''
        );
      } catch (error) {
        console.error('Error fetching open positions:', error);
        setOpenPositions([]);
        setPositionsNotice('Open positions are unavailable for the selected workspace right now.');
      }
    };
  
    fetchOpenPositions();
  }, [refreshTrigger, workspace]);

  useEffect(() => {
    if (openPositions.length > 0) {
      const unique = Array.from(new Set(openPositions.map(pos => pos.symbol)));
      setAvailableSymbols(['ALL', ...unique]);
    }
  }, [openPositions]);
  
  useEffect(() => {
    if (selectedSymbol === 'ALL') {
      setSignalRequestsDynamic(signalRequests);
    } else {
      const filtered = signalRequests.filter(req => req.symbol === selectedSymbol);
      setSignalRequestsDynamic(filtered);
    }
  }, [selectedSymbol, signalRequests]);

  if (loading) return <div className="loading-message">Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  const handleModalComponentSelect = (componentName,actionSymbol) => {
    if (componentName === 'ManualHedge' ) {
      setIsManualHedgeModalOpen(true);
      setActionSymbol(actionSymbol);
    } else if ( componentName === 'SignalDCA' ) {
      setIsSignalDCAModalOpen(true);
      setActionSymbol(actionSymbol);
    } else if ( componentName === 'SignalPartialTakeProfit' ) {
      setIsSignalTPModalOpen(true);
      setActionSymbol(actionSymbol);
    }  else if ( componentName === 'HedgePartialTakeProfit' ) {
      setIsHedgeTPModalOpen(true);
      setActionSymbol(actionSymbol);
    }  else if ( componentName === 'SignalResistanceLevel' ) {
      setIsSignalResistanceLevelModalOpen(true);
      setActionSymbol(actionSymbol);
    }  else if ( componentName === 'HedgeResistanceLevel' ) {
      setIsHedgeResistanceLevelModalOpen(true);
      setActionSymbol(actionSymbol);
    } else if ( componentName === 'RepelTradeHedgeCloser' ) {
      setIsRepelTradeHedgeCloserModalOpen(true);
      setActionSymbol(actionSymbol);
    } else if ( componentName === 'HedgeToSignalCovert' ) {
      setIsHedgeToSignalConvertModalOpen(true);
      setActionSymbol(actionSymbol);
    }        
  };

  const closeSignalDCAModal = () => {
    setIsSignalDCAModalOpen(false);
    setSignalPositionrefreshTrigger(prev => prev + 1);
  };

  const closeSignalTPModal = () => {
    setIsSignalTPModalOpen(false);
    setSignalPositionrefreshTrigger(prev => prev + 1);
  };

  const closeSignalResistanceLevelModal = () => {
    setIsSignalResistanceLevelModalOpen(false);
    setSignalPositionrefreshTrigger(prev => prev + 1);
  };

  const closeManualHedgeModal = () => {
    setIsManualHedgeModalOpen(false);
    setHedgePositionrefreshTrigger(prev => prev + 1 );
  };

  const closeHedgeTPModal = () => {
    setIsHedgeTPModalOpen(false);
    setHedgePositionrefreshTrigger(prev => prev + 1 );
  };

  const closeRepelTradeHedgeCloserModal = () => {
    setIsRepelTradeHedgeCloserModalOpen(false);
  };

  const closeHedgeToSignalConvertModal = () => {
    setIsHedgeToSignalConvertModalOpen(false);
  };

  const closeHedgeResistanceLevelModal = () => {
    setIsHedgeResistanceLevelModalOpen(false);    
    setHedgePositionrefreshTrigger(prev => prev + 1 );
  };

  return (    
    <div>      
            {positionsNotice && <div className="warning-text">{positionsNotice}</div>}
            {
              <div>
                    <div style={{ padding: '10px', backgroundColor: '#222', color: 'white', display: 'flex', alignItems: 'center' }}>
                        {/* existing Filter by Symbol */}
                        <label htmlFor="symbolSelector" style={{ marginRight: '16px' }}>
                          Filter by Symbol:
                          <select
                            id="symbolSelector"
                            value={selectedSymbol}
                            onChange={e => setSelectedSymbol(e.target.value)}
                            style={{ marginLeft: '8px' }}
                          >
                            {availableSymbols.map((sym, i) => (
                              <option key={i} value={sym}>{sym}</option>
                            ))}
                          </select>
                        </label>

                        {/* new Sort by */}
                        <label htmlFor="sortBy" style={{ marginRight: '8px' }}>
                          Sort by:
                          <select
                            id="sortBy"
                            value={sortBy}
                            onChange={e => {
                              setSortBy(e.target.value);
                              setSortDir('asc');      // reset to ascending on new field
                            }}
                            style={{ margin: '0 8px' }}
                          >
                            <option value="">None</option>
                            <option value="symbol">Symbol</option>
                            <option value="unRealizedPL">UnRealized PL</option>
                            <option value="margin">Margin</option>
                          </select>
                        </label>

                        {/* direction toggle */}
                        {sortBy && (
                          <button
                            style={{ ...unifiedButtonStyle, padding: '4px 8px' }}
                            onClick={() => setSortDir(dir => dir === 'asc' ? 'desc' : 'asc')}
                          >
                            {sortDir === 'asc' ? '↑' : '↓'}
                          </button>
                        )}
                      </div>


                  <br/>
                  {sortedRequests.map((signalRequest, index) => (
                        <div key={index} className="symbol-section">
                           {/*  <h3 style={{
                                background: '#212121',
                                color: '#fff',
                                fontSize: '16px',
                                fontWeight: '600',
                                padding: '10px',
                                borderRadius: '5px',
                                marginBottom: '5px',
                                }}>

                            {signalRequest.symbol}
                            {signalRequest.repelTrade === 'true' ? '  -  Repel Trade ' : signalRequest.repelTrade === 'lateRepel' ? '  - Mid Repel Trade ' : ''}                    
                            </h3> */}
                            <br/>
                            <h2 className="page-title">Signal Position</h2>
                            <SignalPositionDetails
                              signalRequestId={signalRequest.signalRequestId}
                              symbol={signalRequest.symbol}
                              side={signalRequest.side}
                              refreshTrigger={refreshTrigger}
                              signalPositionrefreshTrigger={signalPositionrefreshTrigger}
                            />                       
                            <br/>
                            <div className="button-container">
                                <button style={unifiedButtonStyle} onClick={() => handleModalComponentSelect('SignalPartialTakeProfit', signalRequest)}>Signal Partial TakeProfit</button>
                                <button style={unifiedButtonStyle} onClick={() => handleModalComponentSelect('SignalDCA', signalRequest)}>Signal DCA</button>
                                <button style={unifiedButtonStyle} onClick={() => handleModalComponentSelect('SignalResistanceLevel', signalRequest)}>Signal Resistance Levels</button>
                                <button style={unifiedButtonStyle} onClick={() => {
                                    navigate(`/app/${workspace.exchange}/${workspace.marketType}/order-history/${signalRequest.signalRequestId}`, {
                                    state: { previousPath: window.location.pathname }
                                    });
                                }}>Order History</button>
                            </div>
                            <br/>
                            
                            {/* todo this code is commented out. but this should be re designed later when hedge management is considered for restructuring  */}
                            {/* <br/>
                            <h2>Hedge Position</h2>        
                            <HedgePositionDetails
                              signalRequestId={signalRequest.signalRequestId}
                              symbol={signalRequest.symbol}
                              side={signalRequest.side}
                              refreshTrigger={refreshTrigger}
                              hedgePositionrefreshTrigger={hedgePositionrefreshTrigger}
                            />
                            <br/>
                            <div className="button-container">
                            <button style={unifiedButtonStyle} onClick={() => handleModalComponentSelect('HedgePartialTakeProfit', signalRequest)}>Hedge Partial TakeProfit</button>
                            <button style={unifiedButtonStyle} onClick={() => handleModalComponentSelect('ManualHedge', signalRequest)}>Manual Hedge</button>
                            <button style={unifiedButtonStyle} onClick={() => handleModalComponentSelect('HedgeResistanceLevel', signalRequest)}>Hedge Resistance Levels</button>
                            <button style={unifiedButtonStyle} onClick={() => handleModalComponentSelect('HedgeToSignalCovert', signalRequest)}>Convert Hedge As Signal</button>

                            {(signalRequest.repelTrade === 'true' || signalRequest.repelTrade === 'lateRepel') && (
                                <button style={unifiedButtonStyle} onClick={() => handleModalComponentSelect('RepelTradeHedgeCloser', signalRequest)}>
                                RepelTrade Hedge Closer
                                </button>
                            )}
                            </div> */}



                            <br/>
                            {/* <br/>
                            <h2>Hedge Offset Levels</h2>
                            <HedgeOffsetLevelDetails
                              signalRequestId={signalRequest.signalRequestId}
                            />  
                            <br/> */} 
                            {/* todo this code is commented out. but this should be re designed later when hedge management is considered for restructuring  */}
                            <br/>
                            <h2 className="page-title">Special Configuration</h2>
                            <SpecialConfigurationDetails
                              signalRequestId={signalRequest.signalRequestId}
                            />         
                            <br/>            
                        </div>
                      ))}
              </div>
             }

             {isManualHedgeModalOpen && (
                <ManualHedge
                  isOpen={isManualHedgeModalOpen}
                  closeModal={closeManualHedgeModal}
                  symbolData={actionSymbol}
                />
              )}        

              {isSignalDCAModalOpen && (
                <SignalDCA
                  isOpen={isSignalDCAModalOpen}
                  closeModal={closeSignalDCAModal}
                  symbolData={actionSymbol}
                />
              )}   

              {isSignalTPModalOpen && (
                <SignalPartialTakeProfit
                  isOpen={isSignalTPModalOpen}
                  closeModal={closeSignalTPModal}
                  symbolData={actionSymbol}
                />
              )} 

              {isHedgeTPModalOpen && (
                <HedgePartialTakeProfit
                  isOpen={isHedgeTPModalOpen}
                  closeModal={closeHedgeTPModal}
                  symbolData={actionSymbol}
                />
              )} 

              {isRepelTradeHedgeCloserModalOpen && (
                <RepelTradeHedgeCloserModal
                  isOpen={isRepelTradeHedgeCloserModalOpen}
                  closeModal={closeRepelTradeHedgeCloserModal}
                  symbolData={actionSymbol}
                />
              )} 

              {isHedgeToSignalConvertModalOpen && (
                <HedgeToSignalConversionModal
                  isOpen={isHedgeToSignalConvertModalOpen}
                  closeModal={closeHedgeToSignalConvertModal}
                  symbolData={actionSymbol}
                />
              )} 

              {isSignalResistanceLevelModalOpen && (
                <SignalResistanceLevel
                  isOpen={isSignalResistanceLevelModalOpen}
                  closeModal={closeSignalResistanceLevelModal}
                  symbolData={actionSymbol}
                />
              )}

                {isHedgeResistanceLevelModalOpen && (
                <HedgeResistanceLevel
                  isOpen={isHedgeResistanceLevelModalOpen}
                  closeModal={closeHedgeResistanceLevelModal}
                  symbolData={actionSymbol}
                />
              )}               
        </div> 
  );
}


export default PositionMainView;
